import React, { useState, useEffect } from 'react';
import { routesAPI } from '@/lib/api';
import { getToday } from '@/lib/utils';
import { 
  ClipboardCheck, Calendar, MapPin, Package, CheckCircle2, 
  AlertTriangle, Truck, Clock, ChevronDown, ChevronUp 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const LogisticaRouteHistoryPage = () => {
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRoute, setExpandedRoute] = useState(null);

  useEffect(() => {
    loadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const loadRoutes = async () => {
    setLoading(true);
    try {
      const response = await routesAPI.getByDate(selectedDate);
      setRoutes(response.data);
    } catch (error) {
      toast.error('Error al cargar historial de rutas');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), "dd MMM yyyy, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), "HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const toggleExpand = (routeId) => {
    setExpandedRoute(expandedRoute === routeId ? null : routeId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tupu-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="pb-24 md:pb-6" data-testid="logistica-route-history-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Historial de Rutas</h1>
        <p className="text-muted-foreground">Registro de rutas iniciadas</p>
      </div>

      {/* Selector de fecha */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <Calendar className="w-5 h-5 text-slate-400" />
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-44"
          />
          <Button variant="outline" onClick={loadRoutes}>
            Buscar
          </Button>
        </div>
      </div>

      {/* Resumen del día */}
      {routes.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total Rutas</p>
            <p className="text-2xl font-bold text-slate-900">{routes.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Unidades Cargadas</p>
            <p className="text-2xl font-bold text-blue-600">
              {routes.reduce((sum, r) => sum + r.total_loaded, 0)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Cumplimiento Prom.</p>
            <p className="text-2xl font-bold text-green-600">
              {Math.round(routes.reduce((sum, r) => sum + r.compliance_percentage, 0) / routes.length)}%
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Con Faltantes</p>
            <p className="text-2xl font-bold text-amber-600">
              {routes.filter(r => r.compliance_percentage < 100).length}
            </p>
          </div>
        </div>
      )}

      {/* Lista de rutas */}
      <div className="space-y-4">
        {routes.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay rutas registradas para esta fecha</p>
          </div>
        ) : (
          routes.map((route) => (
            <div
              key={route.id}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden"
            >
              {/* Header de la ruta */}
              <div 
                className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleExpand(route.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      route.compliance_percentage >= 100 ? 'bg-green-100' :
                      route.compliance_percentage >= 80 ? 'bg-amber-100' : 'bg-red-100'
                    }`}>
                      {route.compliance_percentage >= 100 
                        ? <CheckCircle2 className="w-6 h-6 text-green-600" />
                        : <AlertTriangle className="w-6 h-6 text-amber-600" />
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <h3 className="font-bold text-slate-900">{route.location_name}</h3>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Salida: {formatTime(route.started_at)}
                        </span>
                        <span>•</span>
                        <span>{route.items.length} productos</span>
                      </div>
                      <p className="text-xs text-slate-400">por {route.created_by_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${
                        route.compliance_percentage >= 100 ? 'text-green-600' :
                        route.compliance_percentage >= 80 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {route.compliance_percentage}%
                      </p>
                      <p className="text-xs text-slate-500">
                        {route.total_loaded} / {route.total_requested} und
                      </p>
                    </div>
                    {expandedRoute === route.id 
                      ? <ChevronUp className="w-5 h-5 text-slate-400" />
                      : <ChevronDown className="w-5 h-5 text-slate-400" />
                    }
                  </div>
                </div>
              </div>

              {/* Detalle expandible */}
              {expandedRoute === route.id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">
                      Ruta iniciada: {formatDateTime(route.started_at)}
                    </span>
                  </div>
                  
                  <h4 className="font-semibold text-sm text-slate-700 mb-3">Detalle de carga</h4>
                  <div className="space-y-2">
                    {route.items.map((item) => (
                      <div 
                        key={item.item_id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          item.difference === 0 ? 'bg-green-50' :
                          item.difference > 0 ? 'bg-blue-50' : 'bg-red-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-slate-900">{item.item_name}</p>
                          <p className="text-xs text-slate-500">{item.item_code}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-slate-600">
                              Pedido: <span className="font-medium">{item.requested_quantity}</span>
                            </span>
                            <span className="text-slate-600">
                              Llevó: <span className="font-bold">{item.loaded_quantity}</span>
                            </span>
                            <span className={`font-bold min-w-[60px] text-right ${
                              item.difference === 0 ? 'text-green-600' :
                              item.difference > 0 ? 'text-blue-600' : 'text-red-600'
                            }`}>
                              {item.difference === 0 ? '✓ OK' : 
                               (item.difference > 0 ? `+${item.difference}` : item.difference)} {item.unit}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {route.notes && (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">Observaciones:</p>
                      <p className="text-sm text-slate-700">{route.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LogisticaRouteHistoryPage;
